import React from "react";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, LineChart, Line, CartesianGrid } from "recharts";
import { LayoutDashboard, TrendingUp, AlertTriangle, Lightbulb, ArrowRight } from "lucide-react";
import { usePoll } from "@/hooks/usePoll";
import { endpoints } from "@/lib/api";
import { Panel, Kpi, PageHeader, StatusBadge, Loading } from "@/components/common";
import { inr, inrL, num, statusOf } from "@/lib/format";
import AIBrief from "@/components/AIBrief";
import ProductionEfficiency from "@/components/ProductionEfficiency";

const UCOLORS = { Refrigeration: "#2563eb", Steam: "#f59e0b", Electricity: "#8b5cf6", Water: "#06b6d4", "Compressed Air": "#10b981" };

export default function EnterpriseCommandCenter() {
  const navigate = useNavigate();
  const { data } = usePoll(endpoints.enterprise, 5000);
  const { data: trendData } = usePoll(endpoints.trend, 5000);

  if (!data) return <Loading />;
  const e = data.enterprise;
  const trend = (trendData?.trend || []).map((d) => ({ ...d, t: new Date(d.t).toLocaleTimeString("en-IN", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" }) }));

  return (
    <div data-testid="enterprise-command-center">
      <PageHeader icon={LayoutDashboard} title="Enterprise Command Center"
        subtitle="Run the business, not the plant — production, true cost, margin & carbon at a glance." />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
        <Kpi testid="kpi-production" label="Production" value={num(e.production_klpd)} unit="KLPD" sub="Milk processed" accent="#2563eb" onClick={() => navigate("/process")} />
        <Kpi testid="kpi-utility-cost" label="Utility Cost" value={inrL(e.utility_cost_l)} unit="/L" sub="True utility cost" accent="#10b981" onClick={() => navigate("/economics")} />
        <Kpi testid="kpi-margin" label="Margin" value={inrL(e.margin_l)} unit="/L" sub={`Revenue ${inrL(e.revenue_l)}`} onClick={() => navigate("/economics")} />
        <Kpi testid="kpi-carbon" label="Carbon" value={e.carbon_l} unit="kgCO₂/L" sub="Carbon intensity" accent="#059669" onClick={() => navigate("/sustainability")} />
        <Kpi testid="kpi-opportunity" label="Opportunity" value={inr(e.opportunity_value_yr)} sub="Identified / year" accent="#7c3aed" onClick={() => navigate("/ai")} />
        <Kpi testid="kpi-risk" label="Financial Risk" value={inr(e.financial_risk_yr)} sub="At-risk / year" accent={e.financial_risk_yr > 0 ? "#ef4444" : "#10b981"} onClick={() => navigate("/ai")} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
        <Panel testid="panel-production-efficiency" title="Production Efficiency" subtitle="OEE at a glance vs world-class baselines" className="xl:col-span-3">
          <ProductionEfficiency production={data.production} />
        </Panel>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
        <Panel testid="panel-opportunities" title="Top Opportunities" subtitle="Quantified savings" className="xl:col-span-1">
          <div className="space-y-2">
            {data.opportunities.slice(0, 5).map((o, i) => (
              <button key={i} onClick={() => navigate("/ai")} className="w-full text-left flex items-start gap-2 p-2 rounded hover:bg-slate-50 transition-colors">
                <Lightbulb size={15} className="text-violet-500 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-slate-800 truncate">{o.title}</div>
                  <div className="text-[11px] text-slate-500">conf {Math.round(o.confidence * 100)}%</div>
                </div>
                <span className="text-[13px] font-bold font-mono text-violet-600 whitespace-nowrap">{inr(o.value_per_year)}</span>
              </button>
            ))}
          </div>
        </Panel>

        <Panel testid="panel-losses" title="Top Losses" subtitle="Active economic loss rate">
          {data.losses.length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center">No active losses — plant near expected. ✓</div>
          ) : (
            <div className="space-y-2">
              {data.losses.map((l, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50">
                  <TrendingUp size={15} className="text-red-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-slate-800 truncate">{l.title}</div>
                    <StatusBadge status={l.severity} />
                  </div>
                  <div className="text-right whitespace-nowrap">
                    <div className="text-[13px] font-bold font-mono text-red-600">{inr(l.loss_per_day)}/d</div>
                    <div className="text-[10px] text-slate-400">{inr(l.loss_per_year)}/yr</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel testid="panel-risks" title="Top Risks" subtitle="High / critical exposure">
          {data.risks.length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center">No high/critical risks. ✓</div>
          ) : (
            <div className="space-y-2">
              {data.risks.map((r, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50">
                  <AlertTriangle size={15} className="text-amber-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-slate-800 truncate">{r.title}</div>
                    <span className="text-[11px] text-slate-500 capitalize">{r.twin}</span>
                  </div>
                  <span className="text-[13px] font-bold font-mono text-amber-600 whitespace-nowrap">{inr(r.financial_risk_yr)}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
        <Panel testid="panel-utility-breakdown" title="Utility Cost Breakdown" subtitle="₹ per litre by utility">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.utility_breakdown} layout="vertical" margin={{ left: 10, right: 20 }}>
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={92} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => inrL(v) + "/L"} />
              <Bar dataKey="inr_per_l" radius={[0, 4, 4, 0]} isAnimationActive={false}>
                {data.utility_breakdown.map((b, i) => <Cell key={i} fill={UCOLORS[b.name] || "#64748b"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel testid="panel-cost-trend" title="Cost & Margin Trend" subtitle="Live ₹/L (last ~30 min)" className="xl:col-span-2">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trend} margin={{ left: 0, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="t" tick={{ fontSize: 10 }} minTickGap={40} />
              <YAxis tick={{ fontSize: 11 }} domain={["auto", "auto"]} />
              <Tooltip formatter={(v) => inrL(v)} />
              <Line type="linear" dataKey="utility_per_l" name="Utility/L" stroke="#10b981" strokeWidth={2} dot={false} isAnimationActive={false} />
              <Line type="linear" dataKey="margin_l" name="Margin/L" stroke="#2563eb" strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <AIBrief />
    </div>
  );
}
